/**
 * Created by Alex Poh. on 20/04/20.
 * Copyright © 2020 Curriculum Ltd. All rights reserved.
 */

import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { of, Subject, switchMap, takeUntil } from 'rxjs';
import { ISAChart, ISAChartSubject } from '../school-admin-dashboard.interface';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { SchoolAdminDashboardService } from '../school-admin-dashboard.service';

@Component({
  selector: 'curr-school-statistic-school-admin',
  templateUrl: './school-statistic-school-admin.component.html',
  styleUrls: ['./school-statistic-school-admin.component.scss']
})
export class SchoolStatisticSchoolAdminComponent implements OnInit {

  @Output() studentsTableFiltersChange = new EventEmitter<{ subject: string; studyYearId: number; courseId: number, schoolYearSettingsId?: number }>();
  @Output() assessmentResultsMarkChange = new EventEmitter<string>();

  assessmentSubjects = [];
  assessmentChartTeachers = [];
  assessmentChartSubjects = [];
  chartAverageBenchmark: string;
  assessmentResultsMark = '';
  assessmentResultDate = '';
  assessmentResultsChart = {
    grades: [],
    averageBenchmark: [],
  };
  assessmentResultsChartMonth = [];
  assessmentChartLoading = true;
  assessmentChartNoData = false;
  assessmentChartSelectedSubject: ISAChartSubject;
  subjectsDropdownDisabled = true;

  studentsTableFilters: { subject: string; studyYearId: number; courseId: number, schoolYearSettingsId?: number };

  unsubscribe: Subject<void> = new Subject<void>();

  constructor(private router: Router, private schoolAdminDashboardService: SchoolAdminDashboardService) {}

  ngOnInit(): void {
    this.getAssessmentChartData();
  }

  assessmentChartTeacherChange(value: ISAChartSubject) {
    this.assessmentChartSubjects = [];
    const subjectsByTeacher = this.assessmentSubjects.filter((item) => item.teacherId === value.teacherId);

    subjectsByTeacher.forEach((item) => {
      this.assessmentChartSubjects.push({ value: item, label: item.label });
    });

    if (this.assessmentChartSubjects.length > 0) {
      this.assessmentChartSubjectsChange(this.assessmentChartSubjects[0].value);
    }

    this.subjectsDropdownDisabled = false;
  }

  assessmentChartSubjectsChange(value: ISAChartSubject) {
    this.assessmentChartSelectedSubject = value;
    this.studentsTableFilters = { subject: value.subject, studyYearId: value.studyYearId, courseId: value.courseId, schoolYearSettingsId: value.schoolYearSettingsId };
    this.studentsTableFiltersChange.emit(this.studentsTableFilters);
    this.getAssessmentChartWithoutSubjects(value.subject, value.studyYearId, value.courseId, value.schoolYearSettingsId);
  }

  private getAssessmentChartData(): void {
    this.schoolAdminDashboardService
      .getAssessmentChartSubjects()
      .pipe(
        switchMap((subjects) => {
          if (subjects.length > 0) {
            this.assessmentSubjects = subjects;

            const uniqueTeachers = subjects.filter(
              (item, index, self) => index === self.findIndex((t) => t.teacherName === item.teacherName));

            uniqueTeachers.forEach((item) => {
              this.assessmentChartTeachers.push({ value: item, label: item.teacherName });
            });

            this.assessmentChartLoading = false;
            return of();
          }
          this.assessmentChartNoData = true;
          return of();
        }),
        takeUntil(this.unsubscribe),
      )
      .subscribe(
        (chartData) => {
          if (chartData) this.processAssessmentChart(chartData);
        },
        (error) => {
          this.assessmentChartLoading = false;
        },
      );
  }

  private getAssessmentChartWithoutSubjects(subject: string, studyYearId: number, courseId: number, schoolYearSettingsId: number): void {
    this.schoolAdminDashboardService
      .getAssessmentChart(subject, studyYearId, courseId, schoolYearSettingsId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (chartData) => {
          if (chartData) this.processAssessmentChart(chartData);
        },
        (error) => {
          this.assessmentChartLoading = false;
        },
      );
  }

  private processAssessmentChart(chartData: ISAChart) {
    this.assessmentResultsMark = chartData.averageGrade;
    this.assessmentResultsMarkChange.emit(this.assessmentResultsMark);
    this.assessmentResultDate = `${moment(chartData.startDate).format('D MMMM YYYY')} - ${moment(chartData.endDate).format('D MMMM YYYY')}`;
    const chartResults = {
      grades: [],
      averageBenchmark: [],
    };
    const month = [];
    const lineChartLabels = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    this.chartAverageBenchmark = chartData.averageBenchmark;
    chartData.grades.forEach((item) => {
      chartResults.averageBenchmark.push(chartData.averageBenchmark);
      if (item.gradeLabel === '-') {
        chartResults.grades.push(null)
      } else {
        chartResults.grades.push(item.gradeLabel)
      };
      month.push(lineChartLabels[item.month - 1]);
    });

    if (chartResults.grades.filter(item => !!item).length === 0) chartResults.averageBenchmark = [];

    this.assessmentResultsChart = chartResults;
    this.assessmentResultsChartMonth = month;
    this.assessmentChartLoading = false;
  }
}
